import { resolvePropertyLink, type PropertyHrefFeed, type PropertyLink } from '../../../common/property-href'

/**
 * Returns the review's own title, or null when there is nothing worth
 * showing: no title at all, or a "fake" title that is just the opening of
 * the content duplicated (PMS feeds commonly derive title = first sentence
 * of the review body).
 *
 * The duplicate check normalizes Unicode and whitespace on both sides and
 * requires a word boundary after the matched prefix, so a real short title
 * ("Great") is not swallowed by content that merely continues the word
 * ("Greater Boston…").
 */
export function displayTitle(review: { title: string | null; content: string }): string | null {
  // API rows are only type-cast, never validated — non-string title/content
  // must degrade instead of throwing.
  const title = typeof review.title === 'string' ? review.title.trim() : ''
  if (!title) return null
  const content = typeof review.content === 'string' ? review.content.trim() : ''
  if (content) {
    const norm = (s: string) => s.normalize('NFKC').replace(/\s+/g, ' ').toLowerCase()
    // Strip ALL terminal Unicode punctuation (\p{P} covers 。 ¡ … etc.) so a
    // punctuation-terminated duplicate ("最高の滞在。") still matches.
    const stripped = norm(title)
    const bare = stripped.replace(/\p{P}+$/u, '').trim()
    const hadTerminalPunct = bare !== stripped
    const body = norm(content)
    if (bare && body.startsWith(bare)) {
      // Combining marks (\p{M}) continue the word: lowercasing e.g. "İ" emits
      // "i" + U+0307, and treating that mark as punctuation would fabricate a
      // boundary. The next character is taken as a full code point so an
      // astral letter can't be split into a surrogate half. Locale case pairs
      // JS can't fold (STRASSE/Straße) fail OPEN — a missed dedup shows a
      // duplicate, it never hides a real title.
      const next = Array.from(body.slice(bare.length, bare.length + 2))[0] ?? ''
      const isBoundary = next === '' || !/[\p{L}\p{N}\p{M}]/u.test(next)
      // When the match only exists because the title's terminal punctuation
      // was ignored, the content must continue with punctuation too ("Why?"
      // is a real title before "Why we loved it", not a duplicate of it).
      if (isBoundary && (!hadTerminalPunct || next === '' || /\p{P}/u.test(next))) return null
    }
  }
  return title
}

/** Up to two initials from the first two words of the reviewer name ("Sarah M." → "SM"). */
export function authorInitials(name: string | null | undefined): string {
  const trimmed = typeof name === 'string' ? name.trim() : ''
  if (!trimmed) return 'A'
  const initials = trimmed
    .split(/\s+/)
    .slice(0, 2)
    // Array.from splits on code points, so an astral first character (emoji,
    // rare CJK) yields the whole character instead of a broken surrogate.
    .map((part) => Array.from(part)[0]?.toUpperCase() ?? '')
    .join('')
  return initials || 'A'
}

/**
 * "March 2026"-style stay date. Date-only strings are formatted in UTC so a
 * first-of-month date can't shift into the previous month in negative-offset
 * timezones; the month is range-checked first because Date.UTC would silently
 * normalize e.g. 2025-00 to December 2024. Locale pinned to en-US (same
 * convention as property-card prices).
 */
export function reviewMonthYear(dateStr: string | null | undefined): string {
  // API reviews are only type-cast, never validated — a null/absent date must
  // degrade to '' instead of throwing and blanking the whole widget.
  if (typeof dateStr !== 'string') return ''
  // Full-shape ISO match (date, or datetime with a real time grammar), so
  // neither "2025-06junk" nor "2025-06-15T0garbage" can sneak a month out of
  // a prefix; year is floored at 100 because Date.UTC treats 0–99 as
  // 1900-relative.
  const trimmed = dateStr.trim()
  const match =
    /^(\d{4})-(\d{2})(?:-(\d{2})(?:[T ](?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d+)?)?(?:Z|[+-](?:[01]\d|2[0-3])(?::?[0-5]\d)?)?)?)?$/.exec(trimmed)
  if (match) {
    const year = Number(match[1])
    const month = Number(match[2])
    const day = match[3] ? Number(match[3]) : 1
    if (year < 100 || month < 1 || month > 12 || day < 1 || day > 31) return ''
    return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(
      'en-US',
      { month: 'long', year: 'numeric', timeZone: 'UTC' },
    )
  }
  // Fallback for non-ISO formats: only strings carrying an explicit timezone
  // are accepted. An ISO-looking reject never reaches V8's lenient parser
  // (it would invent a date from "2025-06junk"), and a zone-less string
  // ("June 1, 2025 00:30") parses machine-locally, which would let SSR and
  // clients disagree on the month.
  if (/^\d{4}-/.test(trimmed)) return ''
  if (!/(?:Z|GMT|UTC|[+-]\d{2}:?\d{2})\s*$/i.test(trimmed)) return ''
  const date = new Date(trimmed)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

/** "4 guests" / "1 guest"; null when the count isn't a positive number. */
export function guestsLabel(count: number | null | undefined): string | null {
  if (typeof count !== 'number' || !Number.isFinite(count) || count <= 0) return null
  return `${count} guest${count === 1 ? '' : 's'}`
}

/**
 * Client-side keyword filter over already-loaded reviews (#1692). A review
 * matches when EVERY whitespace-separated word of the query appears,
 * case-insensitively, somewhere in its text, guest name, or property name.
 * An empty query returns the input untouched. API rows are only type-cast,
 * so non-string fields degrade to empty instead of throwing.
 */
export function filterReviews<T extends { content?: unknown; author?: unknown; property_name?: unknown }>(
  reviews: T[],
  query: string,
): T[] {
  const words = (query || '').normalize('NFKC').toLowerCase().split(/\s+/).filter(Boolean)
  if (!words.length) return reviews
  const str = (v: unknown) => (typeof v === 'string' ? v : '')
  return reviews.filter((r) => {
    const hay = `${str(r.content)} ${str(r.author)} ${str(r.property_name)}`.normalize('NFKC').toLowerCase()
    return words.every((w) => hay.includes(w))
  })
}

/**
 * Href of the review's property page (#1692), resolved the way the explorer
 * card does (`common/property-card/card-parts.tsx` usePropertyUrl): the feed's
 * `pdp` route with `[slug]` filled by the feed's slug for the property
 * (Central #3704 `property_slug`). Null when the row has no usable slug — the
 * caller renders plain text rather than a dead link. Rows from a Central
 * older than #3704 have no `property_slug` key at all and resolve to null too.
 */
export function reviewPropertyHref(pdpRoute: string, review: { property_slug?: unknown }): string | null {
  return reviewPropertyLink(pdpRoute, review)?.href ?? null
}

/**
 * The same link as {@link reviewPropertyHref}, resolved through the shared
 * property-href resolver when the feed is given (#1714): a property the feed
 * maps to its own page (External Property Pages) links there. Without a feed
 * this is exactly the PDP route with the slug filled in.
 */
export function reviewPropertyLink(
  pdpRoute: string,
  review: { property_slug?: unknown },
  feed?: PropertyHrefFeed | null,
): PropertyLink | null {
  const slug = typeof review.property_slug === 'string' ? review.property_slug.trim() : ''
  if (!slug) return null
  return resolvePropertyLink({ feed, slug, pdpRoute })
}
